import { TICK_RATE } from "../constants.js";

/**
 * Weapon statistics. A weapon is DATA, not code: every number the simulation
 * needs lives in this struct, so the server, the client's prediction and the
 * item database read the same fields.
 */

/**
 * Everything the simulation needs to resolve a shot, WITHOUT the identity — so
 * the fire path can take a weapon whose numbers were computed
 * (`EffectiveWeapon`, with the cartridge folded in). A plain `WeaponDef`
 * satisfies it.
 */
export interface WeaponStats {
  /** Rounds per minute. The server treats this as a CEILING, not a cadence. */
  rpm: number;
  magSize: number;
  reloadSec: number;
  /** Metres per second at the muzzle; drives travel time and drop. */
  muzzleVelocity: number;
  /** Damage at point blank, before armour and range falloff. */
  damage: number;
  /** Penetration power, compared against armour and material thickness. */
  penPower: number;
  /** Damage is undiminished up to here, in metres. */
  falloffStartM: number;
  /** ...and has decayed to `falloffMinMult` by here. */
  falloffEndM: number;
  falloffMinMult: number;
  /**
   * How handy the gun is, 0–100 — THE handling stat attachments push around
   * (`StatMods.ergoAdd`), from which `adsTimeSec` is derived.
   */
  ergonomics: number;
  /**
   * Seconds to bring the gun to the shoulder. DERIVED, NEVER AUTHORED: always
   * `adsTimeForErgo(ergonomics)`, which `validateItemDb` enforces — editing it
   * directly is a boot failure.
   */
  adsTimeSec: number;
  /** Cone half-angle applied to hip-fired shots, degrees. */
  hipSpreadDeg: number;
  /**
   * Range at which the sights are zeroed, metres — see `sightZeroElevationRad`.
   */
  zeroRangeM: number;
}

/** An authored weapon: its stats plus the identity the item DB keys it by. */
export interface WeaponDef extends WeaponStats {
  id: string;
  name: string;
}

/** ADS time at ergonomics 0 / 100, seconds. Linear in between. */
export const ADS_TIME_MAX_SEC = 0.65;
export const ADS_TIME_MIN_SEC = 0.2;

/**
 * The ONE mapping from ergonomics to time-to-shoulder: a straight line from
 * `ADS_TIME_MAX_SEC` at ergo 0 to `ADS_TIME_MIN_SEC` at 100, flat outside, so
 * "+8 ergonomics" means the same milliseconds on every gun. Deliberately SLOWER
 * than the old per-weapon numbers — the shoulder is a commitment. Non-finite
 * input collapses to ergo 0 rather than NaN.
 */
export function adsTimeForErgo(ergonomics: number): number {
  const e = Number.isFinite(ergonomics)
    ? ergonomics < 0
      ? 0
      : ergonomics > 100
        ? 100
        : ergonomics
    : 0;
  return ADS_TIME_MAX_SEC + (ADS_TIME_MIN_SEC - ADS_TIME_MAX_SEC) * (e / 100);
}

/** The carbine's handling, named so the literal below cannot drift from the
 *  ADS time derived from it. */
const CARBINE_ERGO = 55;

/**
 * The Jagdkarabiner — a hunting carbine. SEMI-AUTOMATIC: `rpm` is not a fire
 * rate but the fastest the server accepts consecutive `fire` messages; quicker
 * is a macro.
 */
export const CARBINE: WeaponDef = {
  id: "carbine",
  name: "Jagdkarabiner",
  rpm: 400,
  magSize: 30,
  reloadSec: 2.4,
  muzzleVelocity: 700,
  damage: 53,
  penPower: 30,
  falloffStartM: 40,
  falloffEndM: 250,
  falloffMinMult: 0.6,
  // Middle of the roster: heavier than the SMG, far handier than the DMR.
  ergonomics: CARBINE_ERGO,
  adsTimeSec: adsTimeForErgo(CARBINE_ERGO),
  hipSpreadDeg: 1.5,
  // Village sightlines: within ±7 mm of the sight line to 60 m, ~5 cm low at
  // 100 m.
  zeroRangeM: 50,
};

/** Registry keyed by `WeaponDef.id`. One entry today; a table tomorrow. */
export const WEAPONS: ReadonlyMap<string, WeaponDef> = new Map<string, WeaponDef>([
  [CARBINE.id, CARBINE],
]);

/** Looks a weapon up by id. Returns `null` for anything unknown — ids arrive
 *  from the wire and must never be able to throw inside the tick loop. */
export function getWeapon(id: string): WeaponDef | null {
  return WEAPONS.get(id) ?? null;
}

/**
 * Damage multiplier at a range: flat 1 to `startM`, linear to `minMult` at
 * `endM`, flat after. Split from `damageMultAtRange` because a projectile
 * carries only the three falloff numbers.
 */
export function falloffMult(
  distM: number,
  startM: number,
  endM: number,
  minMult: number,
): number {
  if (distM <= startM) return 1;
  // Degenerate authoring (end <= start) collapses to a step, rather than
  // producing a NaN that would silently zero someone's damage.
  if (endM <= startM) return minMult;
  if (distM >= endM) return minMult;
  const t = (distM - startM) / (endM - startM);
  return 1 + (minMult - 1) * t;
}

export function damageMultAtRange(def: WeaponStats, distM: number): number {
  return falloffMult(distM, def.falloffStartM, def.falloffEndM, def.falloffMinMult);
}

/**
 * Minimum spacing between two shots, in TICKS — a FLOAT on purpose: 400 rpm is
 * 4.5 ticks, and rounding either way hands out a 10 % rate change. The server
 * accumulates elapsed ticks as a float.
 */
export function fireIntervalTicks(def: WeaponStats): number {
  return (60 / def.rpm) * TICK_RATE;
}
